// src/services/labService.js
// All Supabase operations for the Challenge Lab feature

import { supabase } from '../supabaseClient';

const VALID_CATEGORIES = ['social', 'creative', 'gym', 'money', 'chaos', 'anti-opt'];

// ─── SUBMISSIONS ──────────────────────────────────────────────────────────────

export const submitChallenge = async ({ userId, username, text, category }) => {
  if (!VALID_CATEGORIES.includes(category)) throw new Error('Invalid category');

  // Enforce: max 3 active submissions per user
  const { count } = await supabase
    .from('challenge_submissions')
    .select('id', { count: 'exact', head: true })
    .eq('submitter_id', userId)
    .eq('status', 'voting');

  if (count >= 3) throw new Error('MAX_SUBMISSIONS_REACHED');

  const { data, error } = await supabase
    .from('challenge_submissions')
    .insert([{
      submitter_id: userId,
      submitter_username: username,
      text: text.trim(),
      category,
    }])
    .select()
    .single();

  if (error) throw error;
  return data;
};

// Submissions currently in voting window (not yet expired, not yet promoted)
export const fetchVotingQueue = async () => {
  const { data, error } = await supabase
    .from('challenge_submissions')
    .select('*')
    .eq('status', 'voting')
    .gt('voting_ends_at', new Date().toISOString())
    .order('votes', { ascending: false });

  if (error) throw error;
  return data || [];
};

// Promoted challenges for the daily rotation
export const fetchPromotedChallenges = async () => {
  const { data, error } = await supabase
    .from('challenge_submissions')
    .select('*')
    .eq('status', 'promoted')
    .order('completion_count', { ascending: false });

  if (error) throw error;
  return data || [];
};

// Hall of Fame: top completed promoted challenges
export const fetchHallOfFame = async (limit = 20) => {
  const { data, error } = await supabase
    .from('challenge_submissions')
    .select('*')
    .eq('status', 'promoted')
    .order('completion_count', { ascending: false })
    .limit(limit);

  if (error) throw error;
  return data || [];
};

// My submissions (all statuses)
export const fetchMySubmissions = async (userId) => {
  const { data, error } = await supabase
    .from('challenge_submissions')
    .select('*')
    .eq('submitter_id', userId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
};

// ─── VOTING ───────────────────────────────────────────────────────────────────

// Returns set of submission IDs the user has voted on
export const fetchMyVotes = async (userId) => {
  const { data, error } = await supabase
    .from('challenge_submission_votes')
    .select('submission_id')
    .eq('user_id', userId);

  if (error) throw error;
  return new Set((data || []).map(r => r.submission_id));
};

export const castVote = async (submissionId) => {
  const { error } = await supabase.rpc('cast_lab_vote', {
    p_submission_id: submissionId,
  });
  if (error) throw error;
};

export const removeVote = async (submissionId) => {
  const { error } = await supabase.rpc('remove_lab_vote', {
    p_submission_id: submissionId,
  });
  if (error) throw error;
};

// ─── PROMOTION ────────────────────────────────────────────────────────────────

// Called on LabView load. Finds expired voting windows, promotes top per category.
// Returns array of promoted submission objects.
export const runPromotionCheck = async () => {
  const { data, error } = await supabase.rpc('run_lab_promotion_check');
  if (error) throw error;
  return data || [];
};

// ─── COMPLETIONS ─────────────────────────────────────────────────────────────

// Record that a user completed a community challenge.
// Returns { newCount, isLegend } for badge checks.
export const recordCompletion = async (submissionId) => {
  const { data, error } = await supabase.rpc('record_community_completion', {
    p_submission_id: submissionId,
  });
  if (error) throw error;
  const result = data?.[0];
  if (!result) throw new Error('Challenge completion was not recorded.');

  return {
    newCount: result.new_count,
    submitterId: result.submitter_id,
    submitterUsername: result.submitter_username,
    isLegend: result.is_legend,
  };
};

// Check if submitter of any promoted challenge should get challenge-maker / legend-maker
export const checkSubmitterBadges = async (userId) => {
  const { data, error } = await supabase
    .from('challenge_submissions')
    .select('id, status, completion_count')
    .eq('submitter_id', userId);

  if (error) return { challengeMaker: false, legendMaker: false };

  const hasPromoted = (data || []).some(s => s.status === 'promoted');
  const hasLegend   = (data || []).some(s => s.completion_count >= 100);

  return { challengeMaker: hasPromoted, legendMaker: hasLegend };
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────

// Returns true if the user has is_premium = true in user_stats
export const fetchUserPremiumStatus = async (userId) => {
  const { data } = await supabase
    .from('user_stats')
    .select('is_premium')
    .eq('user_id', userId)
    .single();
  return data?.is_premium ?? false;
};

export const CATEGORY_COLORS = {
  social:    { text: 'text-blue-400',   bg: 'bg-blue-900',   border: 'border-blue-500'   },
  creative:  { text: 'text-pink-400',   bg: 'bg-pink-900',   border: 'border-pink-500'   },
  gym:       { text: 'text-red-400',    bg: 'bg-red-900',    border: 'border-red-500'    },
  money:     { text: 'text-green-400',  bg: 'bg-green-900',  border: 'border-green-500'  },
  chaos:     { text: 'text-purple-400', bg: 'bg-purple-900', border: 'border-purple-500' },
  'anti-opt':{ text: 'text-orange-400', bg: 'bg-orange-900', border: 'border-orange-500' },
};

export const timeUntilExpiry = (votingEndsAt) => {
  const ms = new Date(votingEndsAt).getTime() - Date.now();
  if (ms <= 0) return 'ENDED';
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (h >= 24) return `${Math.floor(h / 24)}D ${h % 24}H`;
  return `${h}H ${m}M`;
};
